import type { HighScore } from './types.ts';

export const HIGH_SCORE_LIMIT = 10;

export type RankedTimes = Pick<
  HighScore,
  'calculatedTimeMs' | 'timeMs' | 'recordedAt'
>;

/** Faster calculated boat time wins; equal boat times prefer faster realtime, then the earlier record. */
export function compareScores(left: RankedTimes, right: RankedTimes): number {
  if (left.calculatedTimeMs !== right.calculatedTimeMs) {
    return left.calculatedTimeMs - right.calculatedTimeMs;
  }

  if (left.timeMs !== right.timeMs) {
    return left.timeMs - right.timeMs;
  }

  return left.recordedAt - right.recordedAt;
}

/** Inserts `incoming` into `existing` and keeps the best `limit` runs (`compareScores` order). */
export function rankScores(
  existing: readonly HighScore[],
  incoming: HighScore,
  limit: number = HIGH_SCORE_LIMIT,
): HighScore[] {
  return [...existing, incoming]
    .sort(compareScores)
    .slice(0, Math.max(0, limit));
}

/** Race time as `m:ss.ttt` (thousandths). */
export function formatElapsed(ms: number): string {
  const totalMs = Math.floor(Math.max(0, ms));
  const millis = totalMs % 1000;
  const totalSeconds = Math.floor(totalMs / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60);

  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

/** Signed gap as `+m:ss.ttt`, `−m:ss.ttt`, `=`, or `—` when there is no comparable clock. */
export function formatRaceGap(behindMs: number | null): string {
  if (behindMs === null) {
    return '—';
  }

  if (behindMs === 0) {
    return '=';
  }

  const sign = behindMs > 0 ? '+' : '−';
  return `${sign}${formatElapsed(Math.abs(behindMs))}`;
}
